import { createFeatureSelector, createSelector } from '@ngrx/store'
import * as reducer from './rental.reducer'
import * as state from './rental.state'
import * as R from 'ramda'
import { Rental } from './rental.models'

export const rentalState = createFeatureSelector<state.State>('rental')

export const selectRentalsLoaded = createSelector(rentalState, (s: state.State) => s.isLoaded)

export const selectRentalImagesLoaded = createSelector(rentalState, (s: state.State) => s.isImageLoaded)

export const selectIsPrimaryImagesLoaded = createSelector(
  rentalState,
  (state: state.State) => state.isPrimaryImagesLoaded
)

export const selectRentalsSubDomainLoaded = createSelector(rentalState, (s: state.State) => !R.isNil(s.subdomain))

export const selectRentalEntities = createSelector(rentalState, reducer.selectEntities)

export const selectAllRentals = createSelector(rentalState, reducer.selectAll)

export const selectAirbnbLinkedRentals = (includeIds: string[] = []) =>
  createSelector(selectAllRentals, (rentals: Rental[]) => {
    return R.filter((r) => !!r.abb_listing_id || includeIds.includes(r.id), rentals)
  })

export const selectRentalByAirbnbListingId = (listingId: string) =>
  createSelector(selectAllRentals, (rentals: Rental[]) => {
    return R.find((r) => r.abb_listing_id === listingId, rentals)
  })

export const selectUnarchivedRentals = createSelector(selectAllRentals, (rentals: Rental[]) =>
  R.filter((r) => !r.archived, rentals)
)

// #region Parent-Child
export const selectRentalChildren = (parentId: string) =>
  createSelector(selectAllRentals, (items) => {
    if (!parentId) return []
    return items?.filter((r) => r.id !== r.parent_id && r.parent_id === parentId)
  })

export const selectRentalWithChildren = (parentId: string) =>
  createSelector(selectAllRentals, (items) => {
    if (!parentId) return []
    return items?.filter((r) => r.id === parentId || r.parent_id === parentId)
  })

export const selectAvailableRentalChildren = (parentId?: string) =>
  createSelector(selectAllRentals, (items) => {
    const parentIds = R.uniq(R.map((r) => r.parent_id, items))
    // not parent self, not a parent, not a multi unit
    return items?.filter((r) => r.id !== parentId && !parentIds.includes(r.id) && !r.multi_unit_id)
  })

export const selectRentalParentChildrenGroups = createSelector(selectAllRentals, (items) => {
  return R.mapObjIndexed(
    (v: Rental[]) => v.filter((t) => t.id !== t.parent_id),
    R.groupBy(
      (t: Rental) => t.parent_id,
      R.filter((t) => !!t.parent_id, items)
    )
  )
})

export const selectAvailableRentalParents = createSelector(selectAllRentals, (items) => {
  // not a child, not in a multi unit group
  return items?.filter((r) => (!r.parent_id || r.id === r.parent_id) && !r.multi_unit_id)
})

// #endregion Parent-Child

// #region Multi-Unit
export const selectRentalsWithoutSubUnits = createSelector(selectAllRentals, (items) => {
  return items?.filter((t) => !t.multi_unit_id || t.id === t.multi_unit_id)
})

export const selectRentalsWithoutMainUnits = createSelector(selectAllRentals, (items) => {
  return items?.filter((t) => !t.multi_unit_id || t.id !== t.multi_unit_id)
})

export const selectAllRentalSubUnits = createSelector(selectAllRentals, (items) => {
  return items?.filter((r) => r.multi_unit_id && r.id !== r.multi_unit_id)
})

export const selectRentalSubUnits = (unitId: string) =>
  createSelector(selectAllRentals, (items) => {
    if (!unitId) return []
    const subunits = items?.filter((r) => r.id !== r.multi_unit_id && r.multi_unit_id === unitId)
    return R.sortBy((r) => r.multi_unit_priority, subunits).reverse()
  })

export const selectRentalWithSubUnits = (unitId: string) =>
  createSelector(selectAllRentals, (items) => {
    if (!unitId) return []
    return items?.filter((r) => r.id === unitId || r.multi_unit_id === unitId)
  })

export const selectAvailableRentalSubUnits = (unitId?: string) =>
  createSelector(selectAllRentals, (items) => {
    return items?.filter((r) => r.id !== unitId && !r.parent_id && (!r.multi_unit_id || r.multi_unit_id === unitId))
  })

export const selectRentalMultiUnitGroups = createSelector(selectAllRentals, (items) => {
  return R.mapObjIndexed(
    (v: Rental[]) => v.filter((t) => t.id !== t.multi_unit_id),
    R.groupBy(
      (t: Rental) => t.multi_unit_id,
      R.filter((t) => !!t.multi_unit_id, items)
    )
  )
})

export const selectAvailableRentalMainUnits = createSelector(selectAllRentals, (items) => {
  // not a subunit, not in a parent-child group
  return items?.filter((r) => (!r.multi_unit_id || r.id === r.multi_unit_id) && !r.parent_id)
})

// #endregion Multi-Unit

export const selectRentalsWithoutGroupItems = createSelector(selectAllRentals, (items) => {
  return items?.filter((r) => !r.multi_unit_id && !r.parent_id)
})

export const selectImageCountsLoaded = createSelector(rentalState, (state: state.State) => state.imageCountsLoaded)
export const selectRentalRatesLoaded = createSelector(rentalState, (state: state.State) => state.isRatesLoaded)

export const selectAllRentalsWithArchived = createSelector(rentalState, (state) =>
  R.concat(R.values(state.entities), state.archivedRentals)
)

export const selectRentalById = (id: string) =>
  createSelector(selectRentalEntities, (entities) => new Rental(entities[id]))

export const selectAllRentalTags = createSelector(selectAllRentals, (rentals) => {
  return R.uniq(R.flatten(R.map((rental) => rental.tags || [], rentals))).sort()
})

export const selectSubdomain = createSelector(rentalState, (state) => state.subdomain)

export const isSavingRental = createSelector(rentalState, (state) => state.isSavingRental)

export const isUpdatingOwners = createSelector(rentalState, (state) => state.isUpdatingOwners)

export const isRestrictingUsers = createSelector(rentalState, (state) => state.isRestrictingUsers)

export const isSavingTags = createSelector(rentalState, (state) => state.isSavingTags)

export const isSavingCustom = createSelector(rentalState, (state) => state.isSavingCustom)

export const isSavingDetailInfo = createSelector(rentalState, (state) => state.isSavingDetailInfo)

export const isSavingRentalInstructions = createSelector(rentalState, (state) => state.isSavingRentalInstructions)

export const isSavingRentalBaseRate = createSelector(rentalState, (state) => state.isSavingBaseRate)

export const selectRentalCities = createSelector(selectAllRentals, (rentals) => {
  return R.pipe(
    R.map((r: Rental) => R.pathOr('', ['address', 'city'], r)),
    R.reject(R.isNil),
    R.reject(R.isEmpty),
    R.uniq,
    R.sort((a, b) => a.toString().localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  )(rentals)
})

export const selectRentalTags = createSelector(selectAllRentals, (rentals): string[] => {
  return R.pipe(
    R.map((r: Rental) => R.pathOr([], ['tags'], r)),
    R.flatten,
    R.reject(R.isNil),
    R.reject(R.isEmpty),
    R.uniq,
    R.sort((a, b) => a.toString().localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  )(rentals)
})

export const selectRentalCitiesWithArchived = createSelector(rentalState, (state) => {
  return R.pipe(
    R.map((r: Rental) => R.pathOr('', ['address', 'city'], r)),
    R.reject(R.isNil),
    R.reject(R.isEmpty),
    R.uniq,
    R.sort((a, b) => a.toString().localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  )(R.concat(R.values(state.entities), state.archivedRentals))
})

export const selectRentalTagsWithArchived = createSelector(rentalState, (state) => {
  return R.pipe(
    R.map((r: Rental) => R.pathOr([], ['tags'], r)),
    R.flatten,
    R.reject(R.isNil),
    R.reject(R.isEmpty),
    R.uniq,
    R.sort((a, b) => a.toString().localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  )(R.concat(R.values(state.entities), state.archivedRentals))
})
